import type { GqlClient } from '@/types/gqlClient'

// Note: these types use string literals for the `account_type` discriminant
// (rather than `typeof AccountType.X`) so the discriminated union narrows
// cleanly inside `switch` blocks. The shared `AccountType` values are typed
// as the full union (see @autoriz/shared/accounts) to support the common
// `.includes(...)` pattern, which would otherwise widen the narrow literal
// here too.

export type ClientProfile = {
  account_type: 'CLIENT'
  client: {
    id: number
    gender: string
    first_name: string
    last_name: string
  }
}

export const getClientProfile = (
  gqlClient: GqlClient,
  accountID: number
): Promise<ClientProfile> => {
  const query = `
  query ($id: Int!) {
    accounts_by_pk(id: $id) {
      account_type
      client {
        id
        gender
        last_name
        first_name
      }
    }
  }
      `
  return gqlClient
    .request<{ accounts_by_pk: ClientProfile }>(query, {
      id: accountID,
    })
    .then((data) => Promise.resolve(data.accounts_by_pk))
}

/**
 * The company's creation rights, read through whichever relation ties the
 * signed-in account to its company (#655).
 *
 * Held on the COMPANY and read THROUGH the salesman — never copied onto the
 * salesman row — so a salesman moved between companies, or added to an already
 * restricted one, is bound immediately with no second write to keep in sync.
 */
export type CompanyCreationRights = {
  can_create_urban_analysis: boolean
  can_create_projects: boolean
}

export type SalesmanProfile = {
  account_type: 'SALESMAN'
  salesmen: {
    id: number
    gender: string
    first_name: string
    last_name: string
    company: ({ id: number } & CompanyCreationRights) | null
  }
}

export const getSalesmanProfile = (
  gqlClient: GqlClient,
  accountID: number
): Promise<SalesmanProfile> => {
  const query = `
  query ($id: Int!) {
    accounts_by_pk(id: $id) {
      account_type
      salesmen {
        id
        gender
        first_name
        last_name
        company {
          id
          can_create_urban_analysis
          can_create_projects
        }
      }
    }
  }
      `
  return gqlClient
    .request<{ accounts_by_pk: SalesmanProfile }>(query, {
      id: accountID,
    })
    .then((data) => Promise.resolve(data.accounts_by_pk))
}

// `companies` has no `corporate_name` — that column lives on `clients`. The
// company's display name is `companies.name`, which is also the only name
// column the SOCIETY select permission exposes. Selecting `corporate_name`
// here made Hasura reject the whole query with `validation-failed`, so a
// signed-in company resolved no profile at all (#379).
//
// The same trap guards the two creation-rights columns below (#655): they
// resolve only because public_companies.yaml exposes them to SOCIETY and — for
// the salesman query above — to SALESMAN. Adding a column here before the
// metadata exposes it does not null the field, it rejects the WHOLE query, and
// every commercial signs in to a nameless shell.
export type CompanyProfile = {
  account_type: 'SOCIETY'
  company: ({
    id: number
    name: string
  } & CompanyCreationRights) | null
}

export const getCompanyProfile = (
  gqlClient: GqlClient,
  accountID: number
): Promise<CompanyProfile> => {
  const query = `
  query ($id: Int!) {
    accounts_by_pk(id: $id) {
      account_type
      company {
        id
        name
        can_create_urban_analysis
        can_create_projects
      }
    }
  }
      `
  return gqlClient
    .request<{ accounts_by_pk: CompanyProfile }>(query, {
      id: accountID,
    })
    .then((data) => Promise.resolve(data.accounts_by_pk))
}

export type CollaboratorProfile = {
  account_type: 'COLLABORATOR'
  collaborator: {
    id: number
    gender: string
    first_name: string
    last_name: string
  }
}

export const getCollaboratorProfile = (
  gqlClient: GqlClient,
  accountID: number
): Promise<CollaboratorProfile> => {
  const query = `
  query ($id: Int!) {
    accounts_by_pk(id: $id) {
      account_type
      collaborator {
        id
        gender
        last_name
        first_name
      }
    }
  }
      `
  return gqlClient
    .request<{ accounts_by_pk: CollaboratorProfile }>(query, {
      id: accountID,
    })
    .then((data) => Promise.resolve(data.accounts_by_pk))
}

export type ArchitectProfile = {
  account_type: 'ARCHITECT'
  architect: {
    id: number
    gender: string
    first_name: string
    last_name: string
  }
}

export const getArchitectProfile = (
  gqlClient: GqlClient,
  accountID: number
): Promise<ArchitectProfile> => {
  const query = `
  query ($id: Int!) {
    accounts_by_pk(id: $id) {
      account_type
      architect {
        id
        gender
        last_name
        first_name
      }
    }
  }
      `
  return gqlClient
    .request<{ accounts_by_pk: ArchitectProfile }>(query, {
      id: accountID,
    })
    .then((data) => Promise.resolve(data.accounts_by_pk))
}
